import {
  isPublicGameInProgressMessage,
  SEAT_MOVED_MESSAGE,
  SEAT_TAKEN_OVER_CLOSE_CODE,
  SEAT_TAKEOVER_METHOD,
  type SeatTakeoverOrigin,
} from "@bing/shared";
import type { Client, Room } from "colyseus.js";

/**
 * THE DOOR'S ANSWER WHEN A NEWER COPY OF THIS PLAYER HOLDS THE SEAT (card-74 round 2, #3) — what
 * {@link takeSeatBack} returns for the game-server's `SEAT_TAKEN_OVER_CLOSE_CODE` refusal, which only a
 * player who holds the chair (their own signed initData) is ever given. The caller takes it as taken
 * over: final, the takeover sentence, nothing saved touched.
 */
export const SEAT_MOVED = "moved" as const;

/**
 * THE DOOR GRANTED A SEAT AND ITS UPGRADE LOST THE RACE (card-9f) — the duplicated reconnect's loser
 * (CLAUDE.md's standing verdict: refused non-terminally, the seat is not lost): «seat reservation
 * expired.» at the socket, or its 4002 close — or the grace ran out on the way
 * ({@link isGraceBoundaryRefusal}). Returned ONLY to a caller that asks (`surfaceLost`), the
 * held-seat return, which asks the door once more for it; every other caller reads null, as before.
 */
export const SEAT_LOST = "lost" as const;

/** Did the consume fail because another socket took the same reservation first? (See {@link SEAT_LOST}.) */
function isLostUpgrade(error: unknown): boolean {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  const message = error instanceof Error ? error.message : String(error);
  return code === 4002 || /seat reservation expired/i.test(message);
}

/**
 * …or did the 60 s grace end between the door's answer and the upgrade? (card-9f review.) Card-74's
 * reservation is granted while the chair is still human; when the grace gives it to a bot before
 * the upgrade reaches `onAuth`, `returning` is false and the room's own hold refuses with the
 * public-game sentence — with Redis down, `onJoin` refuses `room "<id>" is locked`. Nothing was
 * charged and the chair is still the player's: the one more ask gets the door's bot-held branch.
 */
function isGraceBoundaryRefusal(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return / is locked$/.test(message) || isPublicGameInProgressMessage(message);
}

/** A join or return the game-server answered with the takeover code (round 2, #3) — see {@link SEAT_MOVED}. */
export class SeatMovedError extends Error {
  readonly code = SEAT_TAKEN_OVER_CLOSE_CODE;
  constructor() {
    super(SEAT_MOVED_MESSAGE);
    this.name = "SeatMovedError";
  }
}

/** Is this refusal the takeover code — a newer copy of this player holds the seat? (Any door: the
 *  `takeover` answer, a `joinById` the locked room's door answered, {@link SeatMovedError}.) */
export function isSeatMoved(error: unknown): boolean {
  return (error as { code?: unknown } | null | undefined)?.code === SEAT_TAKEN_OVER_CLOSE_CODE;
}

/**
 * ASK FOR THE SEAT BACK FROM AN OLDER COPY OF THIS APP (card-74, the operator 2026-10-20).
 *
 * A return to a saved table (`rejoin`: «ادامه بازی», the straight-back, the crash screen) reconnects
 * with the device's ticket — and an older copy of the app whose socket is still alive holds that seat
 * with no reservation behind it, so the reconnect is refused 4214 however long it waits. This asks the
 * game-server's `takeover` door instead, with THIS launch's initData: it reserves a seat only for a
 * player who already holds a chair at that table, from a launch no older than the copy it lets go
 * (`apps/game-server/src/rooms/seat-takeover.ts`), so it can never seat anyone new, and a game-server
 * that has no such door answers «invalid method».
 *
 * Never throws: null for every refusal and every failure — the caller then goes on exactly as before —
 * except the one refusal that ends the return: {@link SEAT_MOVED}, a newer copy holds the seat.
 * The colyseus.js 0.15 private `http` + `consumeSeatReservation` pair, as `withConnectTickets` uses it;
 * the reservation carries no token, so nothing secret rides the socket URL.
 */
export async function takeSeatBack(
  client: Client,
  roomId: string,
  initData: string,
  origin: SeatTakeoverOrigin,
  options: { consume?: boolean; surfaceLost: true },
): Promise<Room | typeof SEAT_MOVED | typeof SEAT_LOST | null>;
export async function takeSeatBack(
  client: Client,
  roomId: string,
  initData: string,
  origin: SeatTakeoverOrigin,
  options?: { consume?: boolean; surfaceLost?: false },
): Promise<Room | typeof SEAT_MOVED | null>;
export async function takeSeatBack(
  client: Client,
  roomId: string,
  initData: string,
  /** Where this ask comes from — on the takeover's record, so a count tells them apart (round 2, #4). */
  origin: SeatTakeoverOrigin,
  /** `consume: false` — learn the answer but leave a granted seat unused (it lapses): the caller may not
   *  take one now (another attempt of its page is still out; round 3, #1). `surfaceLost: true` — a lost
   *  upgrade answers {@link SEAT_LOST} instead of null (card-9f). */
  options: { consume?: boolean; surfaceLost?: boolean } = {},
): Promise<Room | typeof SEAT_MOVED | typeof SEAT_LOST | null> {
  if (initData.length === 0 || roomId.length === 0) return null;
  const http = (client as { http?: { post?: unknown } }).http;
  if (typeof http?.post !== "function" || typeof client.consumeSeatReservation !== "function") {
    return null;
  }
  try {
    const response = await client.http.post(`matchmake/${SEAT_TAKEOVER_METHOD}/${roomId}`, {
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ initData, origin }),
    });
    // A refusal is `{ code, error }` and nothing else; only a reservation carries a session and a room.
    const answer = response.data as { sessionId?: unknown; room?: unknown; code?: unknown };
    if (isSeatMoved(answer)) return SEAT_MOVED;
    if (typeof answer.sessionId !== "string" || !answer.room) return null;
    if (options.consume === false) return null;
    return await client.consumeSeatReservation(answer);
  } catch (error) {
    // …a reservation that LOST THE RACE to a newer copy before it was used: the game-server's `onJoin`
    // refuses it with the takeover code (round 3, #3).
    if (isSeatMoved(error)) return SEAT_MOVED;
    // …or to another socket of this player on the same reservation, or to the grace's end (card-9f,
    // only when asked).
    return options.surfaceLost === true && (isLostUpgrade(error) || isGraceBoundaryRefusal(error))
      ? SEAT_LOST
      : null;
  }
}
